import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaArrowLeft, FaShoppingBag, FaCreditCard } from 'react-icons/fa';
import { getCartItems, saveCartItems, clearCart, updateCartItemQuantity, removeFromCart } from '../utils/cartUtils';
import './Cart.css';
import config from '../services/config.json';

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load cart items from localStorage on component mount
  useEffect(() => {
    loadCartItems();
  }, []);

  // Listen for cart updates from other components
  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartItems();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const loadCartItems = () => {
    try {
      const items = getCartItems();
      console.log('Loaded cart items from localStorage:', items);
      setCartItems(items);
      setLoading(false);
    } catch (error) {
      console.error('Error loading cart items:', error);
      setCartItems([]);
      setLoading(false);
    }
  };

  const updateQuantity = (itemIndex, newQuantity) => {
    if (newQuantity < 1) return;
    
    updateCartItemQuantity(itemIndex, newQuantity);
    loadCartItems(); // Reload cart items
  };

  const removeItem = (itemIndex) => {
    removeFromCart(itemIndex);
    loadCartItems(); // Reload cart items
  };

  const handleClearCart = () => {
    clearCart();
    loadCartItems(); // Reload cart items
  };

  const subtotal = cartItems.reduce((sum, item) => sum + ((item.OfferPrice || item.Price) * item.Quantity), 0);
  const shipping = 30; // Delivery charges: 30 AED
  const total = subtotal + shipping;

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="loading-cart">
            <h2>Loading your cart...</h2>
            <p>Please wait while we load your items.</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <FaShoppingBag />
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <Link to="/products" className="btn btn-primary">
              <FaArrowLeft className="btn-icon" />
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Shopping Cart</h1>
          <p className="page-subtitle">Review your items and proceed to checkout</p>
        </div>

        <div className="cart-content">
          {/* Cart Items */}
          <div className="cart-items-section">
            <div className="cart-header">
              <h2>Cart Items ({cartItems.length})</h2>
              <button className="clear-cart-btn" onClick={handleClearCart}>
                <FaTrash />
                Clear Cart
              </button>
            </div>

            <div className="cart-items">
              {cartItems.map((item, index) => (
                <div key={`${item.Id}-${item.ColourId}-${item.SizeId}-${item.ZipType}-${index}`} className="cart-item">
                  <div className="item-image2">
                    <img src={config.apiUrl + item.Image} alt={item.Name} />
                  </div>
                  
                  <div className="item-details">
                    <div className="item-info">
                      <h3 className="item-name">{item.Name}</h3>
                      
                      {/* Display customization details */}
                      <div className="item-customizations">
                        {item.ColorName && (
                          <span className="customization-detail">Color: {item.ColorName}</span>
                        )}
                        {item.SizeName && (
                          <span className="customization-detail">Size: {item.SizeName}</span>
                        )}
                        {item.ZipType && (
                          <span className="customization-detail">Type: {item.ZipType}</span>
                        )}
                        {item.SelectedCustomizations && item.SelectedCustomizations.length > 0 && (
                          <div className="selected-customizations">
                            <span>Customizations:</span>
                            {item.SelectedCustomizations.map((custom, idx) => (
                              <div key={idx} className="customization-item">
                                <span className="customization-name">
                                  {custom.Customization} (+ AED {custom.Price})
                                </span>
                                {custom.Type == "Text"  &&(
                                  <span className="customization-text">
                                    Text: {custom.TextValue}
                                  </span>
                                )}
                                 {custom.Type == "Color"  && (
                                  <span className="customization-text">
                                    Color: {custom.ColorValue}
                                  </span>
                                )}
                                {custom.PhotoValue && custom.PhotoValue.length > 0 && (
                                  <div className="customization-photo">
                                    <img 
                                      src={config.apiUrl + custom.PhotoValue} 
                                      alt={custom.Customization}
                                      width="50" 
                                      height="50"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="item-price">
                        AED {item.OfferPrice || item.Price}
                        {item.OfferPrice && item.OfferPrice !== item.Price && (
                          <span className="original-price">AED {item.Price}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="item-quantity">
                      <label>Quantity:</label>
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => updateQuantity(index, item.Quantity - 1)}
                        >
                          -
                        </button>
                        <span className="quantity-value">{item.Quantity}</span>
                        <button 
                          className="quantity-btn"
                          onClick={() => updateQuantity(index, item.Quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="item-total">
                      <span>Total: AED {((item.OfferPrice || item.Price) * item.Quantity).toFixed(2)}</span>
                    </div>
                    
                    <button 
                      className="remove-item-btn"
                      onClick={() => removeItem(index)}
                    >
                      <FaTrash />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h2>Order Summary</h2>
            
            <div className="summary-items">
              <div className="summary-item">
                <span>Subtotal ({cartItems.length} items)</span>
                <span>AED {subtotal.toFixed(2)}</span>
              </div>
              
              <div className="summary-item">
                <span>Delivery Charges</span>
                <span>{shipping} AED</span>
              </div>
              
              <div className="summary-item total">
                <span>Total</span>
                <span>AED {total.toFixed(2)}</span>
              </div>
            </div>

            <div className="checkout-actions">
              <button className="btn btn-primary checkout-btn" onClick={handleCheckout}>
                <FaCreditCard />
                Proceed to Checkout
              </button>
              
              <Link to="/products" className="btn btn-secondary">
                <FaArrowLeft className="btn-icon" />
                Continue Shopping
              </Link>
            </div>

            <div className="payment-methods">
              <h3>Accepted Payment Methods</h3>
              <div className="payment-icons">
                <span>💳</span>
                <span>🏦</span>
                <span>📱</span>
                <span>💳</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
